const RELOAD_MARKER_PREFIX = 'baba.lazyRouteReloaded:'

type StorageLike = {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

type WindowLike = {
  location: {
    pathname?: string
    reload(): void
  }
  sessionStorage?: StorageLike
}

function defaultWindow(): WindowLike | undefined {
  return typeof window === 'undefined' ? undefined : window
}

function safeSessionStorage(win: WindowLike): StorageLike | null {
  try {
    return win.sessionStorage ?? null
  } catch {
    return null
  }
}

function reloadMarkerKey(win: WindowLike): string {
  return `${RELOAD_MARKER_PREFIX}${win.location.pathname || '/'}`
}

function lazyImportErrorMessage(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  return String(error)
}

export function isFreshAssetImportError(error: unknown): boolean {
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|Load failed/i.test(
    lazyImportErrorMessage(error),
  )
}

export async function importWithFreshAssetReload<T>(
  importer: () => Promise<T>,
  win: WindowLike | undefined = defaultWindow(),
): Promise<T> {
  try {
    const module = await importer()
    if (win) safeSessionStorage(win)?.removeItem(reloadMarkerKey(win))
    return module
  } catch (error) {
    if (!win || !isFreshAssetImportError(error)) throw error

    const storage = safeSessionStorage(win)
    const markerKey = reloadMarkerKey(win)
    if (storage?.getItem(markerKey) === '1') throw error

    storage?.setItem(markerKey, '1')
    win.location.reload()
    return new Promise<T>(() => {})
  }
}
